import React from "react";
import styled from "styled-components/macro";
import { Product } from "features/ProductReel/types";
import { useDispatch } from "react-redux";
import { colors } from "theme/Theme";
import { showRootModal } from "features/rootModal/rootModalSlice";
import { Link } from "react-router-dom";
import Labels from "components/Label/Labels";
import { addProductToCompare } from "features/compare/compareSlice";
import { MdCompareArrows, MdAddShoppingCart } from "react-icons/md";
import { PageWithFilterType } from "features/Filters/types";
import { getLabels } from "./utils";
import climaIMG from "../../assets/images/clima_img.png";
import DualPrice, { EuroPrice } from "components/Price";

interface ProductCardProps {
  item: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ item }) => {
  const dispatch = useDispatch();
  const isAvailable = Number(item.quantity) > 0;
  const hasPromotion = Number(item.price_promotion) > 0;

  const addItem = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isAvailable) {
      dispatch(
        showRootModal({
          modalType: "CHECKOUT",
          modalProps: { item, pageType: PageWithFilterType.category },
        })
      );
    }
  };

  const addItemForComparison = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    dispatch(addProductToCompare(item));
  };

  const labels = getLabels(item);
  const productPath = `/product/${encodeURIComponent(item.alias)}`;
  const specifications = [
    item.quadrature && item.quadrature !== "-1"
      ? item.quadrature
      : null,
    item.power_btu || null,
    item.energy_class_cooling || null,
  ].filter(Boolean) as string[];

  return (
    <ProductContainer className={`restore-${item._id}`}>
      <ProductInfoContainer>
        <LinkTo
          to={productPath}
          state={{ prevUrl: window.location.pathname }}
          aria-label={`Виж подробности за ${item.title}`}
        >
          <UpperSection>
            {labels.length > 0 && (
              <LabelsContainer>
                <Labels labels={labels} muted />
              </LabelsContainer>
            )}

            <ImageFrame>
              <ProductImage
                alt={item.images?.alt || item.title}
                src={
                  item.images
                    ? `data:${item.images.contentType};base64,${item.images.image}`
                    : climaIMG
                }
                loading="lazy"
                decoding="async"
              />
            </ImageFrame>

            <ProductTitle>{item.title}</ProductTitle>

            {specifications.length > 0 && (
              <CardLabels aria-label="Основни характеристики">
                {specifications.map((specification, index) => (
                  <InfoLabel key={`${specification}-${index}`}>
                    {specification}
                  </InfoLabel>
                ))}
              </CardLabels>
            )}
          </UpperSection>
        </LinkTo>

        <BottomSection>
          <PriceAndAvailability>
            <PriceContainer>
              <CurrentPrice aria-label="Текуща цена">
                <DualPrice
                  bgn={hasPromotion ? item.price_promotion! : item.price}
                  eur={
                    hasPromotion ? item.price_promotion_eur : item.price_eur
                  }
                />
              </CurrentPrice>
              {hasPromotion && (
                <PreviousPrice aria-label="Стара цена">
                  <EuroPrice bgn={item.price} eur={item.price_eur} />
                </PreviousPrice>
              )}
            </PriceContainer>

            <Availability $available={isAvailable}>
              <AvailabilityDot aria-hidden="true" />
              {isAvailable ? "В наличност" : "Няма наличност"}
            </Availability>
          </PriceAndAvailability>

          <ButtonsContainer>
            <AddButton
              type="button"
              disabled={!isAvailable}
              onClick={addItem}
              aria-label={`Добави ${item.title} в количката`}
            >
              <MdAddShoppingCart aria-hidden="true" />
              <span>Добави</span>
            </AddButton>
            <CompareButton
              type="button"
              onClick={addItemForComparison}
              aria-label={`Добави ${item.title} за сравнение`}
              title="Добави за сравнение"
            >
              <MdCompareArrows aria-hidden="true" />
            </CompareButton>
          </ButtonsContainer>
        </BottomSection>
      </ProductInfoContainer>
    </ProductContainer>
  );
};

export const ProductContainer = styled.article`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e6edf2;
  border-radius: 14px;
  box-shadow: 0 3px 14px rgba(25, 25, 25, 0.07);
  transition: border-color 180ms ease, box-shadow 180ms ease,
    transform 180ms ease;

  &:focus-within {
    border-color: ${colors.light_blue};
    box-shadow: 0 0 0 3px rgba(0, 152, 255, 0.16),
      0 8px 24px rgba(25, 25, 25, 0.1);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      border-color: #c8dbe8;
      box-shadow: 0 10px 28px rgba(25, 25, 25, 0.12);
    }
  }
`;

export const ProductInfoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
`;

export const LabelsContainer = styled.div`
  position: absolute;
  z-index: 2;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;

  & > * {
    margin: 0;
    border-radius: 6px;
    box-shadow: 0 2px 7px rgba(25, 25, 25, 0.14);
  }
`;

export const UpperSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  height: 100%;
`;

const ImageFrame = styled.div`
  display: flex;
  width: 100%;
  aspect-ratio: 4 / 3;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
`;

export const ProductTitle = styled.h2`
  display: -webkit-box;
  min-height: 3em;
  margin: 16px 16px 0;
  overflow: hidden;
  color: ${colors.darkest};
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.5;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px 16px;
  border-top: 1px solid #edf1f4;
`;

export const CardLabels = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 28px;
  margin: 12px 16px 16px;
  padding: 0;
  list-style: none;
`;

export const InfoLabel = styled.li`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 8px;
  color: ${colors.blue};
  background: ${colors.lightest_blue};
  border: 1px solid #d9effa;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25;
`;

const PriceAndAvailability = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

export const PriceAndButtons = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

export const PriceContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: row wrap;
  align-items: baseline;
  justify-content: flex-start;
  column-gap: 8px;
  row-gap: 2px;
  min-width: 0;

  .price-eur {
    font-size: 1em;
  }
`;

const PreviousPrice = styled.span`
  color: ${colors.darkest_gray};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: line-through;
  white-space: nowrap;
`;

const CurrentPrice = styled.span`
  color: ${colors.darkest};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
`;

const Availability = styled.span<{ $available: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding-bottom: 2px;
  color: ${({ $available }) => ($available ? "#167253" : colors.darkest_gray)};
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
`;

const AvailabilityDot = styled.span`
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  background: currentColor;
  border-radius: 50%;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: stretch;
  gap: 8px;

  .card-button {
    display: inline-flex;
    min-width: 46px;
    min-height: 46px;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    color: ${colors.blue};
    background: #ffffff;
    border: 1px solid #c9e4f5;
    border-radius: 9px;
    cursor: pointer;
    transition: color 160ms ease, background-color 160ms ease,
      border-color 160ms ease;

    &:hover {
      color: #ffffff;
      background: ${colors.blue};
      border-color: ${colors.blue};
    }
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 650;
  line-height: 1;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease,
    border-color 160ms ease, transform 160ms ease;

  svg {
    width: 21px;
    height: 21px;
    flex: 0 0 auto;
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 152, 255, 0.22);
    outline-offset: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(:disabled):hover {
      transform: translateY(-1px);
    }
  }
`;

const AddButton = styled(ActionButton)`
  flex: 1;
  color: #ffffff;
  background: ${colors.blue};
  border: 1px solid ${colors.blue};

  &:not(:disabled):hover {
    background: #006fbb;
    border-color: #006fbb;
  }

  &:disabled {
    color: ${colors.darkest_gray};
    background: ${colors.gray};
    border-color: ${colors.gray};
    cursor: not-allowed;
  }
`;

const CompareButton = styled(ActionButton)`
  width: 46px;
  flex: 0 0 46px;
  padding: 0;
  color: ${colors.blue};
  background: #ffffff;
  border: 1px solid #b9ddf3;

  &:hover {
    color: #ffffff;
    background: ${colors.blue};
    border-color: ${colors.blue};
  }
`;

const ProductImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  padding: 18px;
  box-sizing: border-box;
  object-fit: contain;
  transition: transform 220ms ease;

  ${ProductContainer}:hover & {
    @media (hover: hover) and (pointer: fine) {
      transform: scale(1.025);
    }
  }
`;

export const LinkTo = styled(Link)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: none;
  }
`;

export default ProductCard;
